import { ChevronDown } from '@carbon/icons-react';
import type { UserProfile } from '@repo/shared/orpc/types';
import { cx } from 'class-variance-authority';
import { ProfilePhoto } from 'component/people';
import { UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { forwardRef } from 'react';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { makeTestID, type TLabel, t } from 'util/stdtypes';

import styles from './profile-button.module.css';

/** @public */
export interface ProfileButtonProps extends Omit<ButtonProps, 'children' | 'isDisabled' | 'aria-label'> {
    profile: UserProfile;
    /**
     * Override the accessible name. Defaults to a translated "Profile menu for
     * {name}" so screen readers announce the button's purpose, not just the
     * user's name.
     */
    ariaLabel?: TLabel;
    testID?: string;
}

const defaultAriaLabel = t('user_info.profile_menu_aria_label', 'Profile menu for {name}');

export const ProfileButton = forwardRef<HTMLButtonElement, ProfileButtonProps>(
    ({ profile, ariaLabel, testID, className, ...rest }, ref) => {
        const accessibleName = useTranslation(ariaLabel ?? defaultAriaLabel, { name: profile.name });
        // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
        const composedClassName = composeRenderProps(className, (callerClass) => cx(styles.button, callerClass));
        return (
            <Button
                aria-label={accessibleName}
                data-testid={makeTestID({ testID, text: profile.name })}
                ref={ref}
                {...rest}
                className={composedClassName}
            >
                <span aria-hidden className={styles.photo}>
                    <ProfilePhoto profile={profile} type='small' />
                </span>
                <UtilityText text={profile.name} type='large' weight='medium' />
                <span aria-hidden className={styles.chevron}>
                    <ChevronDown size={16} />
                </span>
            </Button>
        );
    },
);
ProfileButton.displayName = 'ProfileButton';
